// src/components/Checkout/CheckoutForm.tsx

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getCountriesWithPlaceholder } from "@/constants/countries";
import { ReceiverAddressFields } from "./ReceiverAddressFields";
import styles from "./Checkout.module.scss";

interface CheckoutFormProps {
  // Form state
  firstName: string;
  setFirstName: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  phone: string;
  handlePhoneChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  email: string;
  setEmail: (value: string) => void;
  country: string;
  setCountry: (value: string) => void;
  city: string;
  setCity: (value: string) => void;
  postal: string;
  setPostal: (value: string) => void;
  address: string;
  setAddress: (value: string) => void;
  comment: string;
  setComment: (value: string) => void;
  useDifferentAddress: boolean;
  setUseDifferentAddress: (value: boolean) => void;

  // Receiver data
  receiverFirstName: string;
  setReceiverFirstName: (value: string) => void;
  receiverLastName: string;
  setReceiverLastName: (value: string) => void;
  receiverPhone: string;
  handleReceiverPhoneChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  receiverEmail: string;
  setReceiverEmail: (value: string) => void;
  receiverCountry: string;
  setReceiverCountry: (value: string) => void;
  receiverCity: string;
  setReceiverCity: (value: string) => void;
  receiverPostal: string;
  setReceiverPostal: (value: string) => void;
  receiverAddress: string;
  setReceiverAddress: (value: string) => void;

  // Handlers
  handleBlurPhone: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;

  // State
  loading: boolean;
  errors: {
    country?: string;
    phone?: string;
    receiverPhone?: string;
    address?: string;
  };
}

export const CheckoutForm: React.FC<CheckoutFormProps> = ({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  phone,
  handlePhoneChange,
  email,
  setEmail,
  country,
  setCountry,
  city,
  setCity,
  postal,
  setPostal,
  address,
  setAddress,
  comment,
  setComment,
  useDifferentAddress,
  setUseDifferentAddress,
  receiverFirstName,
  setReceiverFirstName,
  receiverLastName,
  setReceiverLastName,
  receiverPhone,
  handleReceiverPhoneChange,
  receiverEmail,
  setReceiverEmail,
  receiverCountry,
  setReceiverCountry,
  receiverCity,
  setReceiverCity,
  receiverPostal,
  setReceiverPostal,
  receiverAddress,
  setReceiverAddress,
  handleBlurPhone,
  onSubmit,
  loading,
  errors,
}) => {
  const { t } = useTranslation("common");

  const countryOptions = useMemo(() => {
    const countries = getCountriesWithPlaceholder(
      t("checkout.form.selectCountry")
    );
    return countries.map((c) =>
      c.isDivider ? (
        <option key={c.code} value="" disabled>
          {c.label}
        </option>
      ) : (
        <option key={c.code} value={c.code}>
          {c.label}
        </option>
      )
    );
  }, [t]);

  return (
    <div className={styles.form} onClick={(e) => e.stopPropagation()}>
      <img
        className={styles.stripeLogo}
        src="/images/stripe.png"
        alt="stripe"
      />
      <form onSubmit={onSubmit}>
        <div className={styles.formRow}>
          <select
            className={styles.select}
            value={country}
            required
            onChange={(e) => setCountry(e.target.value)}
          >
            {countryOptions}
          </select>
        </div>

        <div className={styles.formRow}>
          <input
            className={styles.w50}
            type="text"
            placeholder={t("checkout.form.firstName")}
            value={firstName}
            required
            autoComplete="given-name"
            onChange={(e) => setFirstName(e.target.value)}
          />
          <input
            className={styles.w50}
            type="text"
            placeholder={t("checkout.form.lastName")}
            value={lastName}
            required
            autoComplete="family-name"
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>

        <div className={styles.formRow}>
          <input
            type="text"
            placeholder={t("checkout.form.address")}
            value={address}
            required
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>

        <div className={styles.formRow}>
          <input
            type="email"
            placeholder={t("checkout.form.email")}
            value={email}
            required
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className={styles.formRow}>
          <input
            type="text"
            placeholder={t("checkout.form.city")}
            value={city}
            required
            autoComplete="address-level2"
            onChange={(e) => setCity(e.target.value)}
          />
          <input
            type="text"
            placeholder={t("checkout.form.postal")}
            value={postal}
            required
            autoComplete="postal-code"
            onChange={(e) => setPostal(e.target.value)}
          />
        </div>

        <div className={styles.formRow}>
          <input
            type="tel"
            placeholder={t("checkout.form.phone")}
            value={phone}
            required
            inputMode="tel"
            onChange={handlePhoneChange}
            onBlur={handleBlurPhone}
            style={{
              backgroundColor: errors.phone ? "#FFB3B3" : "",
              outline: errors.phone ? "1px solid red" : "",
            }}
          />
        </div>

        <div className={styles.formRow}>
          <textarea
            placeholder={t("checkout.form.comment")}
            value={comment}
            rows={4}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        <div className={styles.formRow}>
          <label className={styles.infoLabel}>
            <input
              type="checkbox"
              checked={useDifferentAddress}
              onChange={() => setUseDifferentAddress(!useDifferentAddress)}
            />
            {t("checkout.form.differentAddress")}
          </label>
        </div>

        {useDifferentAddress && (
          <ReceiverAddressFields
            receiverCountry={receiverCountry}
            setReceiverCountry={setReceiverCountry}
            receiverFirstName={receiverFirstName}
            setReceiverFirstName={setReceiverFirstName}
            receiverLastName={receiverLastName}
            setReceiverLastName={setReceiverLastName}
            receiverAddress={receiverAddress}
            setReceiverAddress={setReceiverAddress}
            receiverEmail={receiverEmail}
            setReceiverEmail={setReceiverEmail}
            receiverCity={receiverCity}
            setReceiverCity={setReceiverCity}
            receiverPostal={receiverPostal}
            setReceiverPostal={setReceiverPostal}
            receiverPhone={receiverPhone}
            handleReceiverPhoneChange={handleReceiverPhoneChange}
            handleBlurPhone={handleBlurPhone}
            errors={errors}
          />
        )}

        <div className={styles.buttonBlock}>
          <button
            className={`commonButton buttonLg ${styles.button} ${styles.payButton}`}
            type="submit"
            name="bestellung"
            disabled={loading}
          >
            {loading ? t("checkout.form.sending") : t("checkout.form.buyNow")}
          </button>
          <p className={styles.infoLabel}>{t("checkout.form.buyInfo")}</p>
          <button
            className={`commonButton buttonLg ${styles.button} ${styles.feedbackButton}`}
            type="submit"
            name="feedback"
            disabled={loading}
          >
            {loading
              ? t("checkout.form.sending")
              : t("checkout.form.requestCallback")}
          </button>
        </div>
      </form>
    </div>
  );
};
